import type { FC } from 'react'
import {
	Box,
	Heading,
	Link as ChakraLink,
	UnorderedList,
	ListItem,
} from '@chakra-ui/react'
import type { ChakraProps } from '@chakra-ui/react'
import { Link, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

interface FilteredLinksProps {
	areaUrl: string
	areaName: string
}

type SizeItem = { min: string; max: string }
const SIZE_ITEMS: SizeItem[] = [
	{ min: '1', max: '2' },
	{ min: '3', max: '5' },
	{ min: '6', max: '10' },
	{ min: '11', max: '20' },
	{ min: '20', max: '' },
]

const useOfficeTypeTranslation = (mainPath: string | undefined) => {
	const { t } = useTranslation()
	if (mainPath == t('coworking-spaces', { ns: 'url' })) {
		return t('Coworking Spaces')
	}
	if (mainPath == t('lediga-kontorshotell', { ns: 'url' })) {
		return t('Kontorshotell')
	}
	return t('Kontor')
}

const Links: FC<FilteredLinksProps & ChakraProps> = ({
	areaUrl,
	areaName,
	...rest
}) => {
	const { t, i18n } = useTranslation()
	const { pathname } = useLocation()
	const pathnames = pathname.split('/')
	const mainPathname =
		i18n.language === 'sv' ? pathnames[1] : `${i18n.language}/${pathnames[2]!}`
	const officeTypeInPath = i18n.language === 'sv' ? pathnames[1] : pathnames[2]!
	const officeType = useOfficeTypeTranslation(officeTypeInPath)

	if (!areaUrl || !mainPathname) {
		return null
	}

	return (
		<Box {...rest}>
			<Heading mb="1rem" size={{ base: 'sm', md: 'md' }}>
				{t('Utforska efter storlek och budget (med priser)')}
			</Heading>
			<UnorderedList display="flex" flexWrap="wrap" gap={3} ml={0}>
				{SIZE_ITEMS.map((size, i) => (
					<ListItem
						key={`filtered-link-${i}`}
						pl={0}
						sx={{ listStyle: 'none' }}
					>
						<ChakraLink
							as={Link}
							to={`/${mainPathname}/${areaUrl}?min_ws=${size.min}${
								size.max ? `&max_ws=${size.max}` : ''
							}`}
							onClick={() => {
								window.scrollTo(0, 0)
							}}
						>
							{t('{{officeType}} {{area}} för {{sizeRange}} personer', {
								officeType: officeType,
								area: areaName,
								sizeRange: size.max
									? `${size.min}-${size.max}`
									: `fler än ${size.min}`,
							})}
						</ChakraLink>
					</ListItem>
				))}
			</UnorderedList>
		</Box>
	)
}

export default Links
